/**
 * GatewayAriHero — public Ari "digital receptionist" kiosk.
 *
 * Locked 2026-06-02 — Ari Gateway Concept doctrine.
 *   Static state shows a heading + invitation + native input cursor INLINE.
 *
 *   ❗ MOBILE FOCUS BUG FIX (2026-06-02 late):
 *      The previous implementation used an off-screen hidden input and
 *      called .focus() from JS on every tap. That caused mobile browsers
 *      to (a) scroll the off-screen input into view (viewport pan-left)
 *      and (b) immediately dismiss the keyboard on the re-render. The
 *      whole hero was also wrapped in onPointerDown which re-triggered
 *      focus on every tap, killing the keyboard.
 *
 *   FIX: A real visible <input> rendered naturally in the document flow
 *   IS the kiosk prompt line. Tap = native focus. No JS focus management.
 *   The outer wrapper has no pointer handlers — only the input is
 *   interactive.
 *
 *   • Tap the prompt line → keyboard opens, stays open
 *   • Tap anywhere else → does nothing
 *   • Submit → Ari answers
 *   • 30s auto-reset starts after answer. Canceled by focus/input.
 *   • Tap the prompt during answer → resets to idle, focuses naturally.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CopyMessageButton from './AriMessageActions';

const API = process.env.REACT_APP_BACKEND_URL;

const STATE = {
  IDLE: 'idle',       // Static home state — prompt + native cursor
  ASKING: 'asking',   // POST in-flight
  ANSWER: 'answer',   // Showing Ari's response
};

const AUTO_RESET_MS = 30000;

const GatewayAriHero = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const resetTimerRef = useRef(null);

  const [state, setState] = useState(STATE.IDLE);
  const [draft, setDraft] = useState('');
  const [answer, setAnswer] = useState('');
  const [guide, setGuide] = useState(null);
  const [offTopic, setOffTopic] = useState(false);

  // ── Auto-reset timer (only runs in ANSWER state; canceled on any interaction) ──
  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const resetToIdle = useCallback(() => {
    clearResetTimer();
    setDraft('');
    setAnswer('');
    setGuide(null);
    setOffTopic(false);
    setState(STATE.IDLE);
    // No JS .focus() — native focus only when the user taps the input
  }, [clearResetTimer]);

  useEffect(() => () => clearResetTimer(), [clearResetTimer]);

  const submit = async () => {
    const q = (draft || '').trim();
    if (!q || state === STATE.ASKING) return;
    clearResetTimer();
    setState(STATE.ASKING);
    try {
      const resp = await fetch(`${API}/api/ari/gateway/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q }),
      });
      if (!resp.ok) {
        setAnswer("Ari can't answer that right now. Please try again in a moment.");
        setGuide(null);
        setOffTopic(false);
      } else {
        const body = await resp.json();
        setAnswer(body.answer || '');
        setGuide(body.guide || null);
        setOffTopic(Boolean(body.off_topic));
      }
    } catch {
      setAnswer("Ari can't reach the network right now. Please try again shortly.");
      setGuide(null);
      setOffTopic(false);
    }
    setState(STATE.ANSWER);
    // Schedule 30s auto-reset (only if user does not interact)
    resetTimerRef.current = setTimeout(resetToIdle, AUTO_RESET_MS);
  };

  // ── Input handlers (native focus only — no JS focus calls) ──
  const handleFocus = () => {
    // User has tapped the prompt line — cancel any pending auto-reset
    clearResetTimer();
    // If we're showing an answer and the user is about to type a new question,
    // clear the answer and switch to idle so the next keystroke replaces the
    // answer with the typing UI.
    if (state === STATE.ANSWER) {
      setAnswer('');
      setGuide(null);
      setOffTopic(false);
      setDraft('');
      setState(STATE.IDLE);
    }
  };

  const handleChange = (e) => {
    clearResetTimer();
    setDraft(e.target.value);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  // ── Shared input element so the cursor stays in the document flow ──
  const promptInput = (placeholderText, testId) => (
    <form
      onSubmit={handleFormSubmit}
      className="w-full max-w-xl mx-auto"
      data-testid="gateway-ari-form"
    >
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholderText}
        maxLength={500}
        inputMode="text"
        enterKeyHint="send"
        aria-label="Ask Ari about RemodDash"
        data-testid={testId}
        className="w-full bg-white/5 border border-purple-300/30 rounded-full outline-none text-center text-slate-100 text-base sm:text-lg font-normal placeholder:text-slate-300/90 caret-purple-400 px-5 py-3 transition-colors focus:bg-white/10 focus:border-purple-300/60"
        // Disable browser auto-correction / capitalization for kiosk feel
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    </form>
  );

  return (
    <div data-testid="gateway-ari-hero" data-state={state}>
      {/* Static heading — locked copy */}
      <h1
        className="text-center font-bold text-white tracking-tight text-5xl sm:text-6xl md:text-7xl leading-none mb-5"
        data-testid="gateway-headline"
      >
        Hi, I&rsquo;m <span style={{ color: '#A78BFA' }}>Ari.</span>
      </h1>

      {/* Body — kiosk panel. Content varies by state. */}
      <div
        className="text-center text-slate-200 text-lg sm:text-xl font-normal mb-10 sm:mb-12 px-4 max-w-3xl mx-auto"
        style={{ minHeight: '6.5em' }}
        data-testid="gateway-ari-body"
      >
        {state === STATE.IDLE && (
          <>
            <p className="text-slate-200 leading-relaxed max-w-2xl mx-auto mb-5" data-testid="gateway-ari-greeting">
              I know everything there is to know about RemodDash, so if you&rsquo;re
              curious about anything, just tap right here and ask me.
            </p>
            {promptInput('Tap here to ask me anything about RemodDash…', 'gateway-ari-input')}
          </>
        )}

        {state === STATE.ASKING && (
          <p className="text-slate-300 italic" data-testid="gateway-ari-loading">
            Ari is thinking…
          </p>
        )}

        {state === STATE.ANSWER && (
          <div data-testid="gateway-ari-answer">
            <p
              className={`text-base sm:text-lg leading-relaxed whitespace-pre-line ${
                offTopic ? 'text-amber-200' : 'text-slate-100'
              }`}
            >
              {answer}
            </p>
            <div className="mt-2">
              <CopyMessageButton text={answer} light testId="gateway-ari-copy" />
            </div>
            {guide && guide.route && (
              <button
                type="button"
                onClick={() => navigate(guide.route)}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-purple-300 hover:text-purple-200 underline-offset-4 hover:underline"
                data-testid="gateway-ari-guide"
              >
                {guide.label}
                <span aria-hidden>→</span>
              </button>
            )}
            <div className="mt-4">
              {promptInput('Ask another question…', 'gateway-ari-input-reset')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GatewayAriHero;
